import React, { useEffect } from 'react';
import { Check, ImageOff, Search } from 'lucide-react';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface Product {
  id: string;
  title: string;
  vendor: string;
  description: string;
  price: string;
  image_url: string;
  product_type: string;
  handle: string;
}

interface BuildCommercialStep2Props {
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: Product[];
  searching: boolean;
  searchError: string;
  onSearch: (query: string) => void;
}

const stripHtml = (html: string) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const truncate = (text: string, max = 120) =>
  text.length <= max ? text : `${text.substring(0, max)}…`;

const BuildCommercialStep2: React.FC<BuildCommercialStep2Props> = ({
  selectedProduct,
  setSelectedProduct,
  searchQuery,
  setSearchQuery,
  searchResults,
  searching,
  searchError,
  onSearch,
}) => {
  useEffect(() => {
    const timeoutId = setTimeout(() => onSearch(searchQuery), 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearch]);

  return (
    <div className="ed-enter">
      <StepHeading
        index={2}
        kicker="Casting the product"
        title="Who gets the spotlight?"
        lede="Search by name, description, price, who created it, category, or anything else on the product. The one you choose shapes every creative decision that follows."
      />

      <div className="ed-block ed-block--sheet">
        <div className="ed-search">
          <span className="ed-search-icon" aria-hidden="true">
            <Search />
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Name, creator, price, description…"
            aria-label="Search products"
            {...noAutofill}
          />
          {searching && <span className="ed-spinner" role="status" aria-label="Searching" />}
        </div>
      </div>

      {searchError && (
        <div className="ed-alert" role="alert" style={{ marginTop: 24 }}>
          <span>{searchError}</span>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="ed-block">
          <span className="ed-kicker">
            {searchResults.length} result{searchResults.length === 1 ? '' : 's'}
          </span>

          <div className="ed-results" role="radiogroup" aria-label="Search results">
            {searchResults.map((product) => {
              const isSelected = selectedProduct?.id === product.id;

              return (
                <div
                  key={product.id}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  className={`ed-result${isSelected ? ' is-selected' : ''}`}
                  onClick={() => setSelectedProduct(product)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedProduct(product);
                    }
                  }}
                >
                  <div className="ed-result-thumb">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" />
                    ) : (
                      <ImageOff size={20} strokeWidth={1.4} aria-hidden="true" />
                    )}
                  </div>

                  <div className="ed-result-body">
                    <span className="ed-result-vendor">{product.vendor || 'SuperPossible'}</span>
                    <h3 className="ed-result-title">{product.title}</h3>
                    <p className="ed-result-desc">{truncate(stripHtml(product.description))}</p>
                    <div className="ed-result-meta">
                      <span className="ed-result-price">${product.price}</span>
                      {product.product_type && (
                        <span className="ed-tag">{product.product_type}</span>
                      )}
                    </div>
                  </div>

                  {isSelected && (
                    <span className="ed-mark" aria-hidden="true">
                      <Check />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {searchQuery && !searching && searchResults.length === 0 && !searchError && (
        <div className="ed-empty" style={{ marginTop: 32 }}>
          <span className="ed-empty-icon" aria-hidden="true">
            <Search />
          </span>
          <h3 className="ed-empty-title">Nothing matched "{searchQuery}"</h3>
          <p className="ed-body">Try the product name, the creator, a price, or a word from the description.</p>
        </div>
      )}

      {!searchQuery && searchResults.length === 0 && (
        <div className="ed-empty" style={{ marginTop: 32 }}>
          <span className="ed-empty-icon" aria-hidden="true">
            <Search />
          </span>
          <h3 className="ed-empty-title">The catalogue is waiting</h3>
          <p className="ed-body">Start typing above to find your leading product.</p>
        </div>
      )}
    </div>
  );
};

export default BuildCommercialStep2;
